import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import { DATE_STATUS_LABEL, label } from '../lib/labels.js';
import { date, dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import '../screens-2a.css';
import { hrefFor } from '../lib/router.js';
import { ContactLinkage } from './ContactLinkage.js';

interface CapabilityItem {
  key: string;
  label: string;
  status: 'ok' | 'failed' | 'manual' | 'unknown';
  detail?: string;
}

interface DataQualityData {
  connections: {
    eshop: { state: string; lastEvent: string | null };
    web: { state: string };
    watchdog: { state: string };
    feed: { state: string; url: string | null };
    smartEmailing: {
      state: string;
      report: { items: CapabilityItem[]; checkedAt?: string } | null;
      checkedAt: string | null;
    };
  };
  inbox: Array<{ status: string; n: number; oldest: string | null }>;
  processingDelaySeconds: number;
  failedEvents: Array<{
    event_id: string;
    source: string;
    type: string;
    aggregate_id: string;
    error: string;
    received_at: string;
  }>;
  itemsWithoutRace: number;
  racesWithoutDate: Array<{
    race_id: string;
    name: string;
    date_status: string;
    expected_start: string | null;
  }>;
  contactsWithoutLanguage: number;
  syncErrors: Array<{
    id: number;
    contact_id: string | null;
    operation: string;
    error: string;
    at: string;
  }>;
}

const connectionTone = (state: string): 'ok' | 'warn' | 'neu' =>
  state === 'napojeno' || state === 'zapnuto' || state === 'nastaveno'
    ? 'ok'
    : state === 'import'
      ? 'warn'
      : 'neu';

export function DataQuality({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [askRetryAll, setAskRetryAll] = useState(false);

  const query = useQuery<DataQualityData>({
    queryKey: ['data-quality'],
    queryFn: () => api.get('/api/data-quality'),
  });
  const retry = useMutation({
    mutationFn: (eventIds?: string[]) =>
      api.post('/api/data-quality/retry', eventIds ? { eventIds } : {}),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['data-quality'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (query.isLoading) return <LoadingState what="kvalitu dat" />;
  if (query.isError)
    return (
      <ErrorState
        title="Kvalitu dat se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const data = query.data;
  if (!data) return <LoadingState />;

  const canRetry = rights.can('retry_events');
  const pending = data.inbox.find((i) => i.status === 'pending')?.n ?? 0;

  return (
    <>
      <PageHeader
        title="Kvalita dat a napojení"
        description={
          <>
            Jestli hub dostává data z e-shopu, webu a SmartEmailingu a jestli v nich něco nechybí.
            Chybná událost má vždy důvod a po opravě dat ji jde zpracovat znovu.{' '}
            <a href={hrefFor('jobs')}>Úlohy a fronta →</a>
          </>
        }
      />

      {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}

      <div className="integr">
        <Panel title="E-shop">
          <StatusBadge
            tone={connectionTone(data.connections.eshop.state)}
            icon={connectionTone(data.connections.eshop.state) === 'ok' ? 'check' : 'info'}
          >
            {data.connections.eshop.state}
          </StatusBadge>
          <span className="note">
            poslední událost: {dateTime(data.connections.eshop.lastEvent, me.defaultTimezone)}
          </span>
        </Panel>
        <Panel title="Web">
          <StatusBadge
            tone={connectionTone(data.connections.web.state)}
            icon={connectionTone(data.connections.web.state) === 'ok' ? 'check' : 'info'}
          >
            {data.connections.web.state}
          </StatusBadge>
          <span className="note">události z webu plní příznaky chování</span>
        </Panel>
        <Panel title="Hlídací psi">
          <StatusBadge
            tone={connectionTone(data.connections.watchdog.state)}
            icon={connectionTone(data.connections.watchdog.state) === 'ok' ? 'check' : 'info'}
          >
            {data.connections.watchdog.state}
          </StatusBadge>
          <span className="note">veřejné adresy pro přihlášení k hlídání, s dvojím potvrzením</span>
        </Panel>
        <Panel title="Přehled dostupnosti vstupenek">
          <StatusBadge
            tone={connectionTone(data.connections.feed.state)}
            icon={connectionTone(data.connections.feed.state) === 'ok' ? 'check' : 'info'}
          >
            {data.connections.feed.state}
          </StatusBadge>
          <span className="note">
            {data.connections.feed.url ?? 'Adresa přehledu není nastavená'}
          </span>
        </Panel>
        <Panel title="SmartEmailing">
          <StatusBadge
            tone={connectionTone(data.connections.smartEmailing.state)}
            icon={connectionTone(data.connections.smartEmailing.state) === 'ok' ? 'check' : 'info'}
          >
            {data.connections.smartEmailing.state}
          </StatusBadge>
          <span className="note">
            {data.connections.smartEmailing.checkedAt
              ? `ověřeno ${dateTime(data.connections.smartEmailing.checkedAt, me.defaultTimezone)}`
              : 'kontrola schopností zatím neproběhla'}
          </span>
        </Panel>
      </div>

      {data.connections.smartEmailing.report ? (
        <Panel title="Kontrola schopností SmartEmailingu">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Bod</th>
                  <th>Výsledek</th>
                  <th>Podrobnost</th>
                </tr>
              </thead>
              <tbody>
                {data.connections.smartEmailing.report.items.map((item) => (
                  <tr key={item.key}>
                    <td>{item.label}</td>
                    <td>
                      {item.status === 'ok' ? (
                        <StatusBadge tone="ok" icon="check">
                          V pořádku
                        </StatusBadge>
                      ) : item.status === 'failed' ? (
                        <StatusBadge tone="bad" icon="alert">
                          Nefunguje
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone="warn" icon="info">
                          Ruční krok
                        </StatusBadge>
                      )}
                    </td>
                    <td className="muted">{item.detail ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      <div className="tiles">
        <Tile
          label="Zpoždění zpracování událostí"
          value={`${num(data.processingDelaySeconds)} s`}
          note={`${num(pending)} čeká ve frontě`}
          bad={data.processingDelaySeconds > 600}
        />
        <Tile
          label="Chybné události"
          value={num(data.failedEvents.length)}
          bad={data.failedEvents.length > 0}
        />
        <Tile
          label="Položky bez závodu"
          value={num(data.itemsWithoutRace)}
          bad={data.itemsWithoutRace > 0}
        />
        <Tile
          label="Závody bez termínu do 120 dní"
          value={num(data.racesWithoutDate.length)}
          bad={data.racesWithoutDate.length > 0}
        />
        <Tile
          label="Kontakty bez jazyka"
          value={num(data.contactsWithoutLanguage)}
          note="šablona padne na angličtinu"
        />
        <Tile
          label="Chyby synchronizace"
          value={num(data.syncErrors.length)}
          bad={data.syncErrors.length > 0}
        />
      </div>

      {data.failedEvents.length > 0 ? (
        <Panel
          title="Chybné události"
          action={
            canRetry ? (
              <button className="btn pri" onClick={() => setAskRetryAll(true)}>
                Zkusit znovu všechny
              </button>
            ) : null
          }
        >
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Zdroj</th>
                  <th>Typ</th>
                  <th>Čeho se týká</th>
                  <th>Chyba</th>
                  <th>Přijato</th>
                  {canRetry ? <th>Akce</th> : null}
                </tr>
              </thead>
              <tbody>
                {data.failedEvents.map((e) => (
                  <tr key={e.event_id}>
                    <td>{e.source}</td>
                    <td>
                      <code>{e.type}</code>
                    </td>
                    <td>
                      <code>{e.aggregate_id}</code>
                    </td>
                    <td className="muted">{e.error}</td>
                    <td>{dateTime(e.received_at, me.defaultTimezone)}</td>
                    {canRetry ? (
                      <td>
                        <button className="btn" onClick={() => retry.mutate([e.event_id])}>
                          Zkusit znovu
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      {data.racesWithoutDate.length > 0 ? (
        <Panel title="Závody bez potvrzeného termínu">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Závod</th>
                  <th>Stav termínu</th>
                  <th>Očekávané konání</th>
                </tr>
              </thead>
              <tbody>
                {data.racesWithoutDate.map((r) => (
                  <tr key={r.race_id}>
                    <td>
                      <a href={hrefFor('races', r.race_id)}>{r.name}</a>
                    </td>
                    <td>{label(DATE_STATUS_LABEL, r.date_status)}</td>
                    <td>{date(r.expected_start, me.defaultTimezone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      {data.syncErrors.length > 0 ? (
        <Panel title="Chyby synchronizace se SmartEmailingem">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Operace</th>
                  <th>Chyba</th>
                  <th>Kontakt</th>
                  <th>Kdy</th>
                </tr>
              </thead>
              <tbody>
                {data.syncErrors.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <code>{e.operation}</code>
                    </td>
                    <td className="muted">{e.error}</td>
                    <td>
                      {e.contact_id ? <a href={hrefFor('customers', e.contact_id)}>karta</a> : '—'}
                    </td>
                    <td>{dateTime(e.at, me.defaultTimezone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}
      <ContactLinkage />
      <ConfirmDialog
        open={askRetryAll}
        title="Zpracovat všechny chybné události znovu?"
        confirmLabel="Zkusit znovu všechny"
        busy={retry.isPending}
        onCancel={() => setAskRetryAll(false)}
        onConfirm={() => {
          retry.mutate(undefined);
          setAskRetryAll(false);
        }}
      >
        Hub zkusí znovu zpracovat všech {num(data.failedEvents.length)} chybných událostí. Události,
        které selžou znovu, zůstanou v seznamu.
      </ConfirmDialog>
    </>
  );
}
